import { useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type IconDef = [string, Record<string, string | number>][];
const P = (d: string): [string, Record<string, string>] => ['path', { d }];
const ICONS: Record<string, IconDef> = {
  play: [['path', { d: 'M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z', fill: 'currentColor', stroke: 'none' }]],
  pause: [P('M8 5v14M16 5v14')],
  flag: [P('M4 22V4M4 4h13l-2 4 2 4H4')],
  plus: [P('M12 5v14M5 12h14')],
  minus: [P('M5 12h14')],
  check: [P('M20 6 9 17l-5-5')],
  x: [P('M18 6 6 18M6 6l12 12')],
  alert: [P('M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z'), P('M12 9v4M12 17h.01')],
  info: [['circle', { cx: 12, cy: 12, r: 10 }], P('M12 16v-4M12 8h.01')],
  lock: [['rect', { x: 4, y: 11, width: 16, height: 10, rx: 2 }], P('M8 11V7a4 4 0 0 1 8 0v4')],
  upload: [P('M12 15V3'), P('m7 8 5-5 5 5'), P('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4')],
  download: [P('M12 3v12M7 10l5 5 5-5M5 21h14')],
  trash: [P('M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6')],
  scissors: [['circle', { cx: 6, cy: 6, r: 3 }], ['circle', { cx: 6, cy: 18, r: 3 }], P('M20 4 8.1 15.9M14.5 14.5 20 20M8.1 8.1 12 12')],
  'arrow-right': [P('M5 12h14M12 5l7 7-7 7')],
  'arrow-left': [P('M19 12H5M12 19l-7-7 7-7')],
  clock: [['circle', { cx: 12, cy: 12, r: 10 }], P('M12 6v6l4 2')],
  back: [P('M3 12a9 9 0 1 0 3-6.7L3 8'), P('M3 3v5h5')],
  forward: [P('M21 12a9 9 0 1 1-3-6.7L21 8'), P('M21 3v5h-5')],
  list: [P('M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01')],
  monitor: [['rect', { x: 2, y: 3, width: 20, height: 14, rx: 2 }], P('M8 21h8M12 17v4')],
  loader: [P('M21 12a9 9 0 1 1-6.2-8.6')],
};
export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 20, strokeWidth = 1.75, label, className }: { name: IconName; size?: number; strokeWidth?: number; label?: string; className?: string }) {
  const parts = ICONS[name] ?? ICONS.info;
  return (
    <svg
      className={cx('hl-icon', className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {parts.map(([tag, attrs], i) => {
        const Tag = tag as 'path';
        return <Tag key={i} {...(attrs as Record<string, string>)} />;
      })}
    </svg>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'overlay';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconAfter?: boolean;
  iconOnly?: boolean;
  kbd?: string;
}

export function Button({ variant = 'secondary', size = 'md', icon, iconAfter, iconOnly, kbd, className, children, type = 'button', ...rest }: ButtonProps) {
  const ic = icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null;
  return (
    <button type={type} {...rest} className={cx('hl-btn', `hl-btn-${variant}`, `hl-btn-${size}`, iconOnly && 'hl-btn-icon', className)}>
      {!iconAfter && ic}
      {!iconOnly && children}
      {iconAfter && ic}
      {kbd && <kbd className="hl-kbd">{kbd}</kbd>}
    </button>
  );
}

export function ScoreBug({ period, time, label, className }: { period?: string; time?: string; label?: ReactNode; className?: string }) {
  return (
    <span className={cx('hl-bug', className)}>
      {period && <span className="hl-bug-period">{period}</span>}
      {time && <span className="hl-bug-time">{time}</span>}
      {label && <span className="hl-bug-label">{label}</span>}
    </span>
  );
}

export type Tone = 'neutral' | 'signal' | 'cue' | 'pitch' | 'warning' | 'danger';

export function Chip({ tone = 'neutral', icon, children, className }: { tone?: Tone; icon?: IconName; children: ReactNode; className?: string }) {
  return (
    <span className={cx('hl-chip', `hl-chip-${tone}`, className)}>
      {icon && <Icon name={icon} size={14} strokeWidth={2} />}
      {children}
    </span>
  );
}

export interface TimelineClip {
  start: number;
  end: number;
  state?: 'included' | 'excluded' | 'selected';
}

export function Timeline({
  duration,
  clips = [],
  markers = [],
  playhead,
  startLabel,
  endLabel,
  label,
  onSeek,
}: {
  duration: number;
  clips?: TimelineClip[];
  markers?: { at: number; label: string }[];
  playhead?: number;
  startLabel?: string;
  endLabel?: string;
  label?: string;
  onSeek?: (sec: number) => void;
}) {
  const d = duration || 1;
  const pct = (s: number) => `${(Math.max(0, Math.min(d, s)) / d) * 100}%`;
  return (
    <div className="hl-timeline" role="img" aria-label={label ?? `${clips.length} clips on the match timeline`}>
      <div
        className="hl-tl-track"
        style={onSeek ? { cursor: 'pointer' } : undefined}
        onPointerDown={
          onSeek
            ? (e) => {
                const r = e.currentTarget.getBoundingClientRect();
                onSeek(((e.clientX - r.left) / r.width) * d);
              }
            : undefined
        }
      >
        {markers.map((m, i) => (
          <span key={`m${i}`} className="hl-tl-marker" style={{ left: pct(m.at) }}>
            <span className="hl-tl-marker-label">{m.label}</span>
          </span>
        ))}
        {clips.map((c, i) => (
          <span key={i} className={cx('hl-tl-clip', c.state && `hl-tl-clip-${c.state}`)} style={{ left: pct(c.start), width: `${(Math.max(0, c.end - c.start) / d) * 100}%` }} />
        ))}
        {playhead !== undefined && <span className="hl-tl-playhead" style={{ left: pct(playhead) }} />}
      </div>
      {(startLabel || endLabel) && (
        <div className="hl-tl-axis">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      )}
    </div>
  );
}

function PitchThumb() {
  return (
    <svg viewBox="0 0 160 90" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth={1.5}>
        <rect x={10} y={8} width={140} height={74} />
        <line x1={80} y1={8} x2={80} y2={82} />
        <circle cx={80} cy={45} r={11} />
      </g>
    </svg>
  );
}

export function ClipRow({
  index,
  title,
  meta,
  duration,
  thumbnail,
  included = true,
  selected,
  tags = [],
  onToggle,
  onSelect,
  onNudge,
  disabled,
}: {
  index: number;
  title: string;
  meta?: string;
  duration?: string;
  thumbnail?: string;
  included?: boolean;
  selected?: boolean;
  tags?: { label: string; tone?: Tone; icon?: IconName }[];
  onToggle?: () => void;
  onSelect?: () => void;
  onNudge?: (edge: 'start' | 'end', delta: -1 | 1) => void;
  disabled?: boolean;
}) {
  const group = (label: string, edge: 'start' | 'end') => (
    <div className="hl-nudge" role="group" aria-label={`${label} of clip ${index}`}>
      <span className="hl-nudge-label" aria-hidden="true">
        {label}
      </span>
      <span className="hl-nudge-btns">
        <button type="button" disabled={disabled} onClick={() => onNudge?.(edge, -1)} aria-label={`${label} one second earlier`}>
          −1s
        </button>
        <button type="button" disabled={disabled} onClick={() => onNudge?.(edge, 1)} aria-label={`${label} one second later`}>
          +1s
        </button>
      </span>
    </div>
  );
  return (
    <div className={cx('hl-clip', selected && 'is-selected', !included && 'is-excluded')} aria-current={selected ? 'true' : undefined}>
      <input type="checkbox" className="hl-check" checked={included} disabled={disabled} onChange={() => onToggle?.()} aria-label={`Include clip ${index}`} />
      <button type="button" className="hl-clip-main" onClick={onSelect} aria-label={`Preview clip ${index}, ${title}`}>
        <span className="hl-clip-thumb">
          {thumbnail ? <img src={thumbnail} alt="" /> : <PitchThumb />}
          {duration && <span className="hl-clip-dur">{duration}</span>}
        </span>
        <span className="hl-clip-text">
          <span className="hl-clip-title">
            <span className="hl-clip-name">{title}</span>
            {tags.map((t, i) => (
              <Chip key={i} tone={t.tone} icon={t.icon}>
                {t.label}
              </Chip>
            ))}
          </span>
          {meta && <span className="hl-clip-meta">{meta}</span>}
        </span>
      </button>
      {onNudge && (
        <div className="hl-clip-nudges">
          {group('Start', 'start')}
          {group('End', 'end')}
        </div>
      )}
    </div>
  );
}

export interface Step {
  label: string;
  state: 'done' | 'current' | 'upcoming' | 'skipped';
  onClick?: () => void;
}

export function Stepper({ steps }: { steps: Step[] }) {
  return (
    <nav className="hl-stepper" aria-label="Steps">
      {steps.map((s, i) => {
        const content = (
          <>
            <span className="hl-step-num">{s.state === 'done' ? <Icon name="check" size={14} strokeWidth={2.5} /> : String(i + 1)}</span>
            {s.label}
            {s.state === 'skipped' && <span className="hl-sr"> (skipped)</span>}
          </>
        );
        const cls = cx('hl-step', `hl-step-${s.state}`);
        return (
          <span key={i} style={{ display: 'contents' }}>
            {i > 0 && <span className="hl-step-sep" aria-hidden="true" />}
            {s.onClick && s.state !== 'skipped' && s.state !== 'current' ? (
              <button type="button" className={cls} onClick={s.onClick} style={{ border: 0, background: 'transparent', cursor: 'pointer' }}>
                {content}
              </button>
            ) : (
              <span className={cls} aria-current={s.state === 'current' ? 'step' : undefined}>
                {content}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

type FieldBase = { label: string; hint?: string; error?: string; suffix?: string; mono?: boolean; className?: string };

export function Field({ label, hint, error, suffix, mono, className, id, ...rest }: FieldBase & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fid = id ?? auto;
  const note = error ?? hint;
  const input = (
    <input
      id={fid}
      {...rest}
      className={cx('hl-input', mono && 'is-mono', error && 'is-invalid')}
      aria-describedby={note ? `${fid}-note` : undefined}
      aria-invalid={error ? true : undefined}
    />
  );
  return (
    <div className={cx('hl-field', className)}>
      <label className="hl-label" htmlFor={fid}>
        {label}
      </label>
      {suffix ? (
        <span className="hl-input-wrap">
          {input}
          <span className="hl-suffix" aria-hidden="true">
            {suffix}
          </span>
        </span>
      ) : (
        input
      )}
      {note && (
        <span id={`${fid}-note`} className={cx('hl-hint', error && 'is-error')}>
          {note}
        </span>
      )}
    </div>
  );
}

export function TextArea({ label, hint, className, id, ...rest }: FieldBase & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={cx('hl-field', className)}>
      <label className="hl-label" htmlFor={fid}>
        {label}
      </label>
      <textarea id={fid} spellCheck={false} {...rest} className="hl-input hl-textarea" aria-describedby={hint ? `${fid}-note` : undefined} />
      {hint && (
        <span id={`${fid}-note`} className="hl-hint">
          {hint}
        </span>
      )}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { value: T; label: string; icon?: IconName }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  const name = useId();
  return (
    <div className={cx('hl-seg', className)} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <label key={o.value} className={cx('hl-seg-opt', o.value === value && 'is-on')}>
          <input type="radio" className="hl-sr" name={name} value={o.value} checked={o.value === value} onChange={() => onChange(o.value)} />
          {o.icon && <Icon name={o.icon} size={16} />}
          {o.label}
        </label>
      ))}
    </div>
  );
}

export function Progress({ value, label = 'Progress', detail }: { value: number; label?: string; detail?: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="hl-progress">
      <div className="hl-progress-head">
        <span className="hl-progress-value">{Math.floor(v)}%</span>
        {detail && <span className="hl-progress-detail">{detail}</span>}
      </div>
      <div className="hl-progress-track" role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <span className="hl-progress-fill" style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

export function Panel({ children, className, ...rest }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLElement>) {
  return (
    <section {...rest} className={cx('hl-panel app-panel', className)}>
      {children}
    </section>
  );
}
